import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { WhatsappStatus } from "@/types/whatsapp";
import { useWhatsappConnection } from "@/hooks/use-whatsapp-connection";
import { useCallback, useState } from "react";
import { router } from "@inertiajs/react";
import {
  CheckCircle2,
  Loader2,
  Phone,
  QrCode,
  TriangleAlert,
  Unplug,
  Wifi,
  WifiOff,
} from "lucide-react";

type Props = {
  whatsapp: WhatsappStatus;
};

export function WhatsappSection({ whatsapp: initialWhatsapp }: Props) {
  const [whatsapp, setWhatsapp] = useState<WhatsappStatus>(initialWhatsapp);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const onQrCodeUpdate = useCallback((qrCode: string) => {
    setWhatsapp((prev) => ({ ...prev, qrCode }));
  }, []);

  const onConnectionUpdate = useCallback(
    (status: WhatsappStatus["status"], number?: string) => {
      setWhatsapp((prev) => ({
        ...prev,
        status,
        number: number ?? prev.number,
        qrCode: status === "connected" ? undefined : prev.qrCode,
      }));
    },
    [],
  );

  useWhatsappConnection({ onQrCodeUpdate, onConnectionUpdate });

  const handleDisconnect = () => {
    setIsDisconnecting(true);
    router.delete("/app/settings/whatsapp", {
      preserveScroll: true,
      onFinish: () => setIsDisconnecting(false),
    });
  };

  const isConnected = whatsapp.status === "connected";
  const isConnecting =
    whatsapp.status === "connecting" || whatsapp.status === "not_found";
  const isError = whatsapp.status === "error";

  return (
    <div className="space-y-6">
      {/* Status card */}
      <Card>
        <CardHeader>
          <CardTitle>WhatsApp</CardTitle>
          <CardDescription>
            Conecte sua conta do WhatsApp para enviar e receber mensagens.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={
                  isConnected
                    ? "flex size-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-500/10"
                    : "flex size-10 items-center justify-center rounded-full bg-muted"
                }
              >
                {isConnected ? (
                  <Wifi className="size-5 text-green-600 dark:text-green-400" />
                ) : (
                  <WifiOff className="size-5 text-muted-foreground" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">Status da conexão</p>
                  <StatusBadge status={whatsapp.status} />
                </div>
                {whatsapp.number && (
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                    <Phone className="size-3.5" />
                    <span>{whatsapp.number}</span>
                  </div>
                )}
              </div>
            </div>
            {isConnected && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={isDisconnecting}>
                    {isDisconnecting ? (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    ) : (
                      <Unplug className="mr-2 size-4" />
                    )}
                    Desconectar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Desconectar WhatsApp</AlertDialogTitle>
                    <AlertDialogDescription>
                      Ao desconectar, você deixará de receber e enviar mensagens
                      pelo WhatsApp. Para reconectar, será necessário escanear o
                      QR Code novamente.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDisconnect}
                      className="bg-destructive text-white hover:bg-destructive/90"
                    >
                      Desconectar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Error state */}
      {isError && (
        <Card className="border-destructive/50">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <div className="flex size-12 items-center justify-center rounded-full bg-destructive/10">
                <TriangleAlert className="size-6 text-destructive" />
              </div>
              <div>
                <p className="font-medium">Falha na conexão com o servidor</p>
                <p className="text-sm text-muted-foreground">
                  Não foi possível conectar à API do WhatsApp. Verifique se o
                  serviço está em execução.
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Recarregue a página para tentar novamente.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* QR Code card */}
      {!isConnected && whatsapp.qrCode && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="size-5" />
              Escanear QR Code
            </CardTitle>
            <CardDescription>
              Abra o WhatsApp no seu celular, vá em{" "}
              <strong>Dispositivos conectados</strong> e escaneie o código
              abaixo.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-xl border bg-white p-4">
                <img
                  src={whatsapp.qrCode}
                  alt="QR Code do WhatsApp"
                  className="size-64"
                />
              </div>
              {isConnecting && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" />
                  Aguardando conexão...
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Connected success state (no QR needed) */}
      {isConnected && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <div className="flex size-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-500/10">
                <CheckCircle2 className="size-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="font-medium">WhatsApp conectado</p>
                <p className="text-sm text-muted-foreground">
                  Suas mensagens estão sendo sincronizadas automaticamente.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: WhatsappStatus["status"] }) {
  switch (status) {
    case "connected":
      return (
        <Badge className="bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-500/10 dark:text-green-400">
          Conectado
        </Badge>
      );
    case "connecting":
      return (
        <Badge variant="outline" className="text-amber-600 border-amber-300 dark:text-amber-400 dark:border-amber-500/30">
          Conectando...
        </Badge>
      );
    case "disconnected":
      return <Badge variant="destructive">Desconectado</Badge>;
    case "not_found":
      return (
        <Badge variant="outline" className="text-muted-foreground">
          Não configurado
        </Badge>
      );
    case "error":
      return <Badge variant="destructive">Erro de conexão</Badge>;
    default:
      return <Badge variant="destructive">Desconectado</Badge>;
  }
}
